// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type { DocumentReference, MedicationRequest, Patient } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PageContainer } from '../../components/layout/PageContainer';
import { getBinaryDownloadUrl } from '../../utils/binary';
import { findCurrentOrderDocument } from '../../utils/prescription-pdf';
import { REFERRAL_DOCUMENT_TYPE_CODE } from '../../utils/referral-pdf';

export function DmeOrderPage(): JSX.Element {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const isReferral = searchParams.get('type') === 'referral';
  const patient = useResource<Patient>(patientId ? { reference: `Patient/${patientId}` } : undefined);
  const prescriptionId = searchParams.get('prescriptionId');
  const prescription = useResource<MedicationRequest>(
    prescriptionId ? { reference: `MedicationRequest/${prescriptionId}` } : undefined
  );
  const [document, setDocument] = useState<DocumentReference>();
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    const loadDocument = async (): Promise<void> => {
      if (!patientId || !prescriptionId) {
        setLoading(false);
        return;
      }
      try {
        // Without an explicit `type=referral`, look across prescription and referral documents so a referral order
        // opened from a link that does not know its type still shows its PDF.
        const found = await findCurrentOrderDocument(
          medplum,
          prescriptionId,
          isReferral ? REFERRAL_DOCUMENT_TYPE_CODE : undefined
        );
        if (!found || disposed) {
          setLoading(false);
          return;
        }
        setDocument(found);
        const url = found.content?.[0]?.attachment?.url;
        if (url) {
          const blob = await medplum.download(getBinaryDownloadUrl(url) as string);
          if (!disposed) {
            setPdfUrl(URL.createObjectURL(blob));
          }
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };
    loadDocument().catch((error: unknown) => {
      Sentry.captureException(error);
      setLoading(false);
    });
    return () => {
      disposed = true;
      setPdfUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return undefined;
      });
    };
  }, [isReferral, medplum, patientId, prescriptionId]);

  if (!patient || !prescription) {
    return (
      <PageContainer>
        <Loader />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={2}>{isReferral ? 'Referral order' : 'DME order · sleep therapy equipment'}</Title>
            <Text c="dimmed">
              {patient.name?.[0]?.text ?? patient.id} · {prescription.medicationCodeableConcept?.text}
            </Text>
          </div>
          <Button variant="default" onClick={() => navigate(`/Patient/${patient.id}/medical-info`)}>
            {isReferral ? 'Back to patient chart' : 'Back to prescriptions'}
          </Button>
        </Group>
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <div>
              <Title order={4}>Order summary</Title>
              <Text size="sm" c="dimmed">
                Read-only after signing. Corrections require an amended order.
              </Text>
            </div>
            <Badge color={prescription.status === 'active' ? 'green' : 'gray'}>{prescription.status}</Badge>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
            <Text size="sm">
              <b>Order:</b> {prescription.medicationCodeableConcept?.text ?? 'DME order'}
            </Text>
            <Text size="sm">
              <b>Ordered:</b> {prescription.authoredOn?.slice(0, 10) ?? '—'}
            </Text>
            <Text size="sm">
              <b>Prescriber:</b> {prescription.requester?.display ?? '—'}
            </Text>
            <Text size="sm">
              <b>Priority:</b> {prescription.priority ?? '—'}
            </Text>
          </SimpleGrid>
          <Divider mb="md" />
          {loading && (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          )}
          {!loading && !document && (
            <Alert color="yellow" title={`${isReferral ? 'Referral' : 'Prescription'} document unavailable`}>
              This order exists, but its signed PDF was not found.
            </Alert>
          )}
          {!loading && document && !pdfUrl && (
            <Alert color="yellow" title="PDF preview unavailable">
              Download document from the patient Documents section.
            </Alert>
          )}
          {pdfUrl && (
            <iframe
              title={isReferral ? 'Referral form PDF' : 'DME prescription PDF'}
              src={pdfUrl}
              style={{ width: '100%', height: '75vh', border: 0 }}
            />
          )}
          {document && (
            <Group justify="flex-end" mt="sm">
              <Anchor href={pdfUrl} target="_blank" rel="noreferrer">
                Signed PDF
              </Anchor>
              <Anchor href={pdfUrl} download={`${document.content?.[0]?.attachment?.title ?? 'dme-order'}.pdf`}>
                Download PDF
              </Anchor>
            </Group>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
